import { useState, useEffect, useRef } from "react";

/*
 //explanations:
 - child component that owns the form element so it receives the browser event.
 - it calls onSubmit(formData) on the parent (no event passed up).
*/
export default function TripForm({ onSubmit }) {
  const startCityRef = useRef(null);
  const endCityRef = useRef(null);

  useEffect(() => {
    if (window.google) return; // already loaded
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_KEY}&libraries=places`;
    script.async = true;
    script.onerror = () => {
      console.warn("Google Maps failed to load - autocomplete disabled");
    };
    document.head.appendChild(script);

    const style = document.createElement("style");
    style.innerHTML = `
  .pac-container {
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
    margin-top: 4px;
    overflow: hidden;
  }
  .pac-item {
    padding: 10px 16px;
    cursor: pointer;
    font-size: 14px;
    color: #374151;
    border-top: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .pac-item:hover {
    background-color: #f0f9ff;
  }
  .pac-item-selected {
    background-color: #e0f2fe;
  }
  .pac-icon {
    display: none;
  }
  .pac-item::before {
    content: '';
    display: inline-block;
    width: 18px;
    height: 18px;
    background-image: url('/favicon.ico');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    flex-shrink: 0;
  }
  .pac-item-query {
    font-weight: 600;
    color: #0f4265;
    font-size: 14px;
  }
  .pac-matched {
    color: #5bc0eb;
  }
  .hdpi .pac-icon {
    display: none;
  }
`;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const initAutocomplete = () => {
      try {
        if (!window.google || !startCityRef.current || !endCityRef.current) {
          setTimeout(initAutocomplete, 300);
          return;
        }

        const options = {
          types: ["(cities)"],
          fields: ["name", "formatted_address", "address_components"],
        };

        const startAC = new window.google.maps.places.Autocomplete(
          startCityRef.current,
          options
        );
        startAC.addListener("place_changed", () => {
          const place = startAC.getPlace();
          const value = place?.formatted_address || place?.name || startCityRef.current?.value || "";
          setFormData((prev) => ({ ...prev, startCity: value }));
        });

        const endAC = new window.google.maps.places.Autocomplete(
          endCityRef.current,
          options
        );
        endAC.addListener("place_changed", () => {
          const place = endAC.getPlace();
          const value = place?.formatted_address || place?.name || endCityRef.current?.value || "";
          setFormData((prev) => ({ ...prev, endCity: value }));
        });
      } catch (err) {
        console.warn("Autocomplete init failed:", err.message);
        // fields still work as plain text
      }
    };

    initAutocomplete();
  }, []);

  const [planningMode, setPlanningMode] = useState("hours");
  const [formData, setFormData] = useState({
    startCity: "",
    endCity: "",
    interests: "",
    drivingHours: "",
    totalDays: "",
    car: "",
    avoidFerries: false,
    twoWayRoute: false,
    currency: "EUR",
    distanceUnit: "km",
  });
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [twoWayTooltipOpen, setTwoWayTooltipOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [distanceOpen, setDistanceOpen] = useState(false);

  const CURRENCIES = [
    { code: "EUR", flag: "eu", name: "Euro" },
    { code: "USD", flag: "us", name: "US Dollar" },
    { code: "GBP", flag: "gb", name: "Pound" },
    { code: "CHF", flag: "ch", name: "Swiss Franc" },
    { code: "CAD", flag: "ca", name: "CA Dollar" },
    { code: "AUD", flag: "au", name: "AU Dollar" },
  ];

  const DISTANCES = [
    { code: "km", label: "KM" },
    { code: "miles", label: "MI" },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const submit = async (e) => {
    e.preventDefault(); // event exists here
    setErr(null);

    // minimal client validation
    if (!formData.startCity.trim() || !formData.endCity.trim()) {
      setErr("Starting city and End city are required.");
      return;
    }
    if (planningMode === "hours") {
      const dh = Number(formData.drivingHours);
      if (!dh || dh < 1 || dh > 14) {
        setErr("Driving hours must be between 1 and 14.");
        return;
      }
    } else {
      const td = Number(formData.totalDays);
      if (!td || td < 1 || td > 30) {
        setErr("Total days must be between 1 and 30.");
        return;
      }
    }

    try {
      setLoading(true);
      await onSubmit({ ...formData, planningMode }); // parent handles API
    } catch (e) {
      setErr(e.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-md bg-white p-6 rounded-2xl shadow-lg space-y-5 mb-8"
    >
      <div>
        <label className="block mb-2 font-semibold">Starting city</label>
        <input
          type="text"
          name="startCity"
          placeholder="e.g. Paris, France"
          ref={startCityRef}
          value={formData.startCity}
          onChange={handleChange}
          className="w-full p-3 border rounded-lg"
          required
        />
      </div>

      <div>
        <label className="block mb-2 font-semibold">End city</label>
        <input
          type="text"
          name="endCity"
          placeholder="e.g. Athens, Greece"
          ref={endCityRef}
          value={formData.endCity}
          onChange={handleChange}
          className="w-full p-3 border rounded-lg"
          required
        />
      </div>

      <div>
        <label className="block mb-2 font-semibold">What would you like to see?</label>
        <input
          type="text"
          name="interests"
          placeholder="e.g. Museums, Nature, Monuments"
          value={formData.interests}
          onChange={handleChange}
          className="w-full p-3 border rounded-lg"
        />
      </div>

      <div>
        <div className="flex rounded-xl border border-gray-200 overflow-hidden mb-2">
          <div className="relative flex-1">
            <button
              type="button"
              onClick={() => setPlanningMode("hours")}
              className={`w-full py-2 text-sm font-medium transition ${
                planningMode === "hours"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-500 hover:bg-background"
              }`}
            >
              🛣️ Flexible
            </button>
          </div>
          <div className="relative flex-1">
            <button
              type="button"
              onClick={() => setPlanningMode("days")}
              className={`w-full py-2 text-sm font-medium transition ${
                planningMode === "days"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-500 hover:bg-background"
              }`}
            >
              📅 Fixed Days
            </button>
          </div>
        </div>
        <label className="block mb-2 font-semibold">
          {planningMode === "hours"
            ? "How many hours do you want to drive per day?"
            : "In how many days do you want to complete the route?"}
        </label>
        {planningMode === "hours" ? (
          <input
            type="number"
            name="drivingHours"
            placeholder="e.g. 6"
            min={1}
            max={14}
            value={formData.drivingHours}
            onChange={handleChange}
            className="w-full p-3 border rounded-lg"
            required
          />
        ) : (
          <input
            type="number"
            name="totalDays"
            placeholder="e.g. 5"
            min={1}
            max={30}
            value={formData.totalDays || ""}
            onChange={handleChange}
            className="w-full p-3 border rounded-lg"
            required
          />
        )}
      </div>

      <div>
        <label className="block mb-2 font-semibold">Car details</label>
        <input
          type="text"
          name="car"
          placeholder="e.g. Fiat Croma 2009, 1.9 Diesel"
          value={formData.car}
          onChange={handleChange}
          className="w-full p-3 border rounded-lg"
        />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="relative">
          <label className="block mb-1 font-semibold text-sm">Currency</label>
          <button
            type="button"
            onClick={() => { setCurrencyOpen((o) => !o); setDistanceOpen(false); }}
            className="w-full p-2 border rounded-lg bg-white text-sm flex items-center justify-between gap-2 text-left"
          >
            <span className="flex items-center gap-2">
              <img
                src={`https://flagcdn.com/20x15/${CURRENCIES.find(c => c.code === formData.currency)?.flag}.png`}
                alt={formData.currency}
                className="w-5 h-auto flex-shrink-0"
              />
              <span className="font-medium">{formData.currency}</span>
            </span>
            <span className="text-gray-400 text-xs">▾</span>
          </button>
          {currencyOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setCurrencyOpen(false)} />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                {CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => { setFormData((prev) => ({ ...prev, currency: c.code })); setCurrencyOpen(false); }}
                    className={`w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-background transition text-left ${
                      formData.currency === c.code ? "bg-accent/10 font-semibold text-primary" : "text-gray-700"
                    }`}
                  >
                    <img
                      src={`https://flagcdn.com/20x15/${c.flag}.png`}
                      alt={c.code}
                      className="w-5 h-auto flex-shrink-0"
                    />
                    <span className="font-medium">{c.code}</span>
                    <span className="text-gray-400 text-xs">{c.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="relative">
          <label className="block mb-1 font-semibold text-sm">Distance</label>
          <button
            type="button"
            onClick={() => { setDistanceOpen((o) => !o); setCurrencyOpen(false); }}
            className="w-full p-2 border rounded-lg bg-white text-sm flex items-center justify-between gap-2 text-left"
          >
            <span className="font-medium">
              {DISTANCES.find(d => d.code === formData.distanceUnit)?.label}
            </span>
            <span className="text-gray-400 text-xs">▾</span>
          </button>
          {distanceOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setDistanceOpen(false)} />
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                {DISTANCES.map((d) => (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => { setFormData((prev) => ({ ...prev, distanceUnit: d.code })); setDistanceOpen(false); }}
                    className={`w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-background transition text-left ${
                      formData.distanceUnit === d.code ? "bg-accent/10 font-semibold text-primary" : "text-gray-700"
                    }`}
                  >
                    <span className="font-medium">{d.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-400 -mt-2">
        Currency shown in guides is approximate. Exchange rates may vary.
      </p>

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          name="avoidFerries"
          checked={formData.avoidFerries}
          onChange={handleChange}
          className="w-4 h-4 accent-primary flex-shrink-0"
        />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium text-gray-700">
              Avoid optional ferries
            </span>
            <span
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTooltipOpen((o) => !o); }}
              className="cursor-pointer text-gray-400 hover:text-primary text-xs font-bold w-4 h-4 rounded-full border border-gray-300 inline-flex items-center justify-center leading-none flex-shrink-0 ml-1"
            >
              ?
            </span>
          </div>
          <em className="text-xs text-gray-500">(keep mandatory ones)</em>
        </div>
        {tooltipOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setTooltipOpen(false)} />
            <div
              className="fixed left-4 right-4 z-20 bg-primary text-white text-xs rounded-xl p-3 shadow-lg leading-relaxed"
              style={{ bottom: "120px" }}
              onClick={(e) => e.stopPropagation()}
            >
              Checking this avoids optional ferries (e.g. convenience
              shortcuts across bays or straits). Mandatory ferries are
              always included, such as routes to islands, or crossings
              where going by road would be geographically unreasonable
              (e.g. Denmark to Sweden).
            </div>
          </>
        )}
      </label>

      <label className="flex items-center gap-3 cursor-pointer mt-2">
        <input
          type="checkbox"
          name="twoWayRoute"
          id="twoWayRoute"
          checked={formData.twoWayRoute}
          onChange={handleChange}
          className="w-4 h-4 accent-primary flex-shrink-0"
        />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium text-gray-700">
              Two-way route
            </span>
            <span
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTwoWayTooltipOpen((o) => !o); }}
              className="cursor-pointer text-gray-400 hover:text-primary text-xs font-bold w-4 h-4 rounded-full border border-gray-300 inline-flex items-center justify-center leading-none flex-shrink-0 ml-1"
            >
              ?
            </span>
          </div>
          <span className="text-xs text-gray-500">(Premium · 2 guides)</span>
        </div>
        {twoWayTooltipOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setTwoWayTooltipOpen(false)} />
                <div
                  className="fixed left-4 right-4 z-20 bg-primary text-white text-xs rounded-xl p-3 shadow-lg leading-relaxed"
                  style={{ bottom: "120px" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Get two complete guides - one for each direction.
                  The return route uses different overnight stops and
                  sightseeing for a fresh experience. Uses 2 guides
                  from your balance.
                </div>
              </>
            )}
      </label>

      <button
        type="submit"
        disabled={loading}
        className={`w-full px-6 py-3 rounded-xl shadow-md text-white transition ${
          loading ? "bg-gray-400 cursor-not-allowed" : "bg-accent hover:bg-highlight"
        }`}
      >
        {loading ? "Generating..." : "Generate Free Guide"}
      </button>

      {err && <p className="text-red-600 text-sm">{err}</p>}
    </form>
  );
}
